import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import type { CuratedAccountPlanQuestion } from '../types';

interface NetworkQuestionCardProps {
  question: CuratedAccountPlanQuestion;
  onSubmit: (questionId: number, answer: string) => Promise<boolean>;
}

const MIN_ANSWER_LENGTH = 30;

const TIER_BADGE: Record<1 | 2 | 3, { label: string; modifier: string }> = {
  1: { label: 'Direct experience', modifier: 'tier-1' },
  2: { label: 'Deal experience', modifier: 'tier-2' },
  3: { label: 'Industry match', modifier: 'tier-3' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NetworkQuestionCard({
  question,
  onSubmit,
}: NetworkQuestionCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const tier = TIER_BADGE[question.match.tier];

  const trimmedLength = answer.trim().length;
  const meetsMinLength = trimmedLength >= MIN_ANSWER_LENGTH;
  const remaining = Math.max(0, MIN_ANSWER_LENGTH - trimmedLength);

  const handleSubmit = async (): Promise<void> => {
    if (!meetsMinLength || submitting) return;
    setSubmitting(true);
    await onSubmit(question.id, answer);
    setSubmitting(false);
  };

  return (
    <article className={`network-question-card${expanded ? ' network-question-card--expanded' : ''}`}>
      <div className="network-question-card__header">
        <div className="network-question-card__company">
          <div className="network-question-card__company-icon">
            <BusinessIcon fontSize="small" />
          </div>
          <div className="network-question-card__company-meta">
            <Typography variant="subtitle2" className="network-question-card__company-name">
              {question.accountPlan.company.companyName}
            </Typography>
            <span className="network-question-card__asked-by">
              Asked by an Account Executive · {formatDate(question.createdAt)}
            </span>
          </div>
        </div>
        <div className={`network-question-card__match network-question-card__match--${tier.modifier}`}>
          <VerifiedIcon fontSize="small" />
          <span>{tier.label}</span>
        </div>
      </div>

      <div className="network-question-card__question-row">
        <HelpOutlineIcon fontSize="small" className="network-question-card__question-icon" />
        <p className="network-question-card__question">{question.question}</p>
      </div>

      <div className="network-question-card__match-reason">
        {question.match.reason}
      </div>

      {!expanded
        ? (
            <div className="network-question-card__actions">
              <Button
                variant="contained"
                color="primary"
                onClick={() => setExpanded(true)}
              >
                Answer this question
              </Button>
            </div>
          )
        : (
            <div className="network-question-card__composer">
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={5}
                maxRows={12}
                placeholder="Share what you know first-hand. Specifics, names, processes — whatever would have helped you when you were running this deal."
                value={answer}
                onChange={event => setAnswer(event.target.value)}
                disabled={submitting}
              />
              <div className="network-question-card__composer-footer">
                <span
                  className={`network-question-card__composer-hint${
                    meetsMinLength ? ' network-question-card__composer-hint--ok' : ''
                  }`}
                >
                  {meetsMinLength
                    ? `${trimmedLength} characters — looking good.`
                    : `${remaining} more character${remaining === 1 ? '' : 's'} to go (min ${MIN_ANSWER_LENGTH}).`}
                </span>
                <div className="network-question-card__composer-actions">
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => { setExpanded(false); setAnswer(''); }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleSubmit}
                    disabled={submitting || !meetsMinLength}
                  >
                    {submitting ? 'Sending…' : 'Send answer'}
                  </Button>
                </div>
              </div>
            </div>
          )}
    </article>
  );
}
